import {
  DeleteOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileZipOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { KnowledgeBaseArticleAttachment } from '@hefe/models';
import { API, KnowledgeBaseApi, currentUserIsAdminSelector } from '@hefe/store';
import { truncateFileName } from '@hefe/utils';
import { Button, Popconfirm, Space, Tooltip, Typography } from 'antd';
import mime from 'mime';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './knowledge-base-attachment.module.scss';

export interface KnowledgeBaseAttachmentComponentProps {
  attachment: KnowledgeBaseArticleAttachment;
}

export default function KnowledgeBaseAttachmentComponent(
  props: KnowledgeBaseAttachmentComponentProps
) {
  const { attachment } = props;

  const userIsAdmin = useSelector(currentUserIsAdminSelector);

  const [isHovered, setIsHovered] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [deleteAttachment, { isLoading: isDeleting }] =
    KnowledgeBaseApi.useDeleteAttachmentMutation();

  const onConfirm = () => {
    // Hier kannst du die Löschfunktion aufrufen
    deleteAttachment(attachment.id);
  };

  const onClick = () => {
    setLoading(true);
    API.files()
      .downloadFile(attachment.file_path)
      .then((response) => {
        const fileExtension = (
          attachment?.file_path?.split('.').pop() ?? ''
        ).toLowerCase();
        const mimeType =
          mime.getType(fileExtension) || 'application/octet-stream';

        const file = new Blob([response.data], { type: mimeType });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      })
      .finally(() => setLoading(false));
  };

  const FileIcon = useMemo(() => {
    if (isDeleting || loading) {
      return LoadingOutlined;
    }

    const fileType = attachment.file_name.split('.').pop();

    switch (fileType) {
      case 'pdf':
        return FilePdfOutlined;
      case 'doc':
      case 'docx':
        return FileWordOutlined;
      case 'xls':
      case 'xlsx':
        return FileExcelOutlined;
      case 'zip':
        return FileZipOutlined;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return FileImageOutlined;
    }

    return FileOutlined;
  }, [isDeleting, loading, attachment.file_name]);

  return (
    <Space
      direction="vertical"
      size={6}
      align="center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ position: 'relative' }}>
        {userIsAdmin && (isHovered || confirmOpen) && (
          <Popconfirm
            title={t('delete') + '?'}
            description={t(
              'knowledge_base.articles.detail_page.attachments.delete.description'
            )}
            onConfirm={() => onConfirm()}
            onOpenChange={(open) => setConfirmOpen(open)}
            disabled={isDeleting}
          >
            <Button
              className={styles['delete-button']}
              type="text"
              icon={<DeleteOutlined style={{ fontSize: 20, color: 'red' }} />}
              loading={isDeleting}
            />
          </Popconfirm>
        )}
        <Button
          type="text"
          icon={<FileIcon style={{ fontSize: 30 }} />}
          onClick={() => onClick()}
        />
      </div>
      <Tooltip title={attachment.file_name}>
        <Typography.Text>
          {truncateFileName(attachment.file_name, 25)}
        </Typography.Text>
      </Tooltip>
    </Space>
  );
}
